/**
 * Lógica de la conexión con Twilio (asistente, diagnóstico y Sandbox). Pura: sin React ni fetch.
 *
 * Las pantallas de `components/conexion/` sólo pintan lo que sale de acá. Todo lo que decide algo
 * (si se puede conectar, qué número va preseleccionado, si el semáforo está en verde, qué botón
 * lleva un paso) está en funciones con test, porque es donde un error cuesta plata o mensajes:
 * un «Listo para enviar» pintado sobre un paso bloqueante en rojo es peor que no mostrar nada.
 *
 * Textos literales de SPEC-estadisticas-conexion §6.
 */

import type {
  AccionPaso, ClaveConfirmacionManual, ConectarEntrada, CuentaPublica, Descubrimiento, DescubrirEntrada, Diagnostico,
  EstadoPaso, FrenoSuperado, GrupoPaso, ModoTransporte, PasoConexion, PasoDiagnostico, ResultadoPruebaSandbox,
} from "../types";
import { validarCredencialesLocal } from "./config";
import { calidadInfo } from "./textos";
import { textoError } from "./respuesta";
import { fechaHora, num } from "../../campana-nueva/utils/format";

// ── Formatos (mismos que exige el backend) ───────────────────────────────────────────────────

export const RE_ACCOUNT_SID = /^AC[0-9a-f]{32}$/;
export const RE_MG = /^MG[0-9a-f]{32}$/;
/** Senders v2 devuelve el XE en hexadecimal que puede venir en mayúsculas (F10): el backend acepta las dos. */
export const RE_XE = /^XE[0-9a-fA-F]{32}$/;
/** Valor del `select` de servicios que significa «crear uno nuevo». No puede chocar con un MG. */
export const SERVICIO_NUEVO = "__nuevo__";
/** Número del Sandbox de Twilio (F21). */
export const NUMERO_SANDBOX = "+14155238886";
export const CONSOLA_TWILIO = "https://console.twilio.com/";

/** `AC…1a2b`: el SID nunca se muestra entero en pantalla (tampoco es secreto, pero no suma). */
export function enmascararSid(sid?: string | null): string {
  const s = String(sid || "").trim();
  if (!s) return "";
  if (s.includes("…") || s.includes("*")) return s;      // ya viene enmascarado del backend
  return s.length > 8 ? `${s.slice(0, 2)}…${s.slice(-4)}` : s;
}

// ── Paso 1: credenciales ─────────────────────────────────────────────────────────────────────

/**
 * Qué mandar a `descubrir`, o por qué todavía no. El token puede faltar SÓLO si ya hay uno
 * guardado (el backend usa el guardado si el Account SID coincide). Nunca se manda un token vacío.
 */
export function armarDescubrir(
  f: { accountSid: string; authToken: string },
  ctx: { tokenGuardado: boolean },
): { ok: true; entrada: DescubrirEntrada } | { ok: false; errores: { accountSid?: string; authToken?: string } } {
  const accountSid = String(f?.accountSid || "").trim();
  const authToken = String(f?.authToken || "").trim();
  const errores: { accountSid?: string; authToken?: string } = {};
  const e = validarCredencialesLocal({ accountSid, authToken });
  if (e.accountSid) errores.accountSid = e.accountSid;
  if (authToken) {
    if (e.authToken) errores.authToken = e.authToken;
  } else if (!ctx?.tokenGuardado) {
    errores.authToken = "Pegá el Auth Token (32 caracteres hexadecimales).";
  }
  if (errores.accountSid || errores.authToken) return { ok: false, errores };
  return { ok: true, entrada: authToken ? { accountSid, authToken } : { accountSid } };
}

/** «Cambiar token» (rotación, §A.4.5): sólo el token, con el mismo formato que el backend. */
export function validarTokenNuevo(token: string): string | undefined {
  return validarCredencialesLocal({ authToken: String(token || "").trim() }).authToken;
}

// ── Paso 2: servicio y número ────────────────────────────────────────────────────────────────

export type NumeroDescubierto = Descubrimiento["numeros"][number];

/** Nombre del Messaging Service que crea el asistente: «Deenex · {marca}» (FriendlyName ≤ 64, F9). */
export function nombreServicioNuevo(marca: string): string {
  const m = String(marca || "").trim().replace(/\s+/g, " ");
  return `Deenex · ${m || "marca"}`.slice(0, 64);
}

/** Límite de Meta en texto: «10.000 por día», «Sin límite», o lo crudo si no se entendió. */
export function textoLimite(n: Pick<NumeroDescubierto, "limite" | "ilimitado" | "limiteRaw">): string {
  if (n.ilimitado) return "Sin límite";
  if (typeof n.limite === "number" && Number.isFinite(n.limite)) return `${num(n.limite)} por día`;
  // «Unavailable» es lo que Twilio dice de un número nuevo (HATSU, 16/10/2026): no es que no lo entendamos. Después de
  // conectar, el límite de WhatsApp Manager se carga en Configuración → Envío.
  if (/^\s*(unavailable|unknown|n\/a|none)\s*$/i.test(String(n.limiteRaw || ""))) return `Twilio todavía no lo informa («${n.limiteRaw}»): se usan 250`;
  return n.limiteRaw ? `sin entender («${n.limiteRaw}»): se usan 250` : "sin dato: se usan 250";
}

/** Una opción del `select` de números: el texto, si está deshabilitada y por qué. */
export function opcionNumero(n: NumeroDescubierto): { valor: string; texto: string; deshabilitado: boolean; motivo: string } {
  const calidad = calidadInfo(n.calidad, n.calidadRaw).label;
  const texto = `${n.numero || n.sid} · ${n.estado || "sin estado"} · calidad ${calidad} · ${textoLimite(n)}`;
  // Fail-closed: sólo se elige un número que Twilio dijo que está operativo.
  const deshabilitado = n.operativo !== true;
  const motivo = deshabilitado
    ? (n.motivosOffline?.filter(Boolean).join(" · ") || `No está activo en Twilio (${n.estado || "sin estado"}).`)
    : "";
  return { valor: n.sid, texto, deshabilitado, motivo };
}

/**
 * Lo que viene preseleccionado: la `sugerencia` del backend, pero sólo si se puede usar de verdad
 * (el número existe y está operativo; el servicio existe). Una sugerencia que no pasa queda vacía:
 * mejor que el operador elija a que conectar algo que después falla.
 */
export function seleccionInicial(d: Descubrimiento | null | undefined): { senderSid: string; servicio: string } {
  if (!d) return { senderSid: "", servicio: "" };
  const s = d.sugerencia || { messagingServiceSid: null, senderSid: null, crearServicio: false, motivo: "" };
  const numero = (d.numeros || []).find((n) => n.sid === s.senderSid && n.operativo === true);
  const senderSid = numero && RE_XE.test(numero.sid) ? numero.sid : "";
  let servicio = "";
  if (s.crearServicio) servicio = SERVICIO_NUEVO;
  else if (s.messagingServiceSid && (d.servicios || []).some((x) => x.sid === s.messagingServiceSid) && RE_MG.test(s.messagingServiceSid)) {
    servicio = s.messagingServiceSid;
  }
  return { senderSid, servicio };
}

/**
 * El cuerpo de `conectar`, o por qué no se puede. `messagingServiceSid` y `crearServicio` son
 * excluyentes (el backend rechaza los dos juntos); el token viaja sólo si hay uno en memoria.
 */
export function armarConectar(
  f: { accountSid: string; authToken?: string; senderSid: string; servicio: string; marca: string; reemplazarEntrada?: boolean },
): { ok: true; entrada: ConectarEntrada } | { ok: false; error: string } {
  const accountSid = String(f?.accountSid || "").trim();
  const senderSid = String(f?.senderSid || "").trim();
  const servicio = String(f?.servicio || "").trim();
  if (!RE_ACCOUNT_SID.test(accountSid)) return { ok: false, error: "Volvé a buscar en Twilio: falta el Account SID." };
  if (!RE_XE.test(senderSid)) return { ok: false, error: "Elegí el número de WhatsApp." };
  if (!servicio) return { ok: false, error: "Elegí el Messaging Service o creá uno nuevo." };
  if (servicio !== SERVICIO_NUEVO && !RE_MG.test(servicio)) return { ok: false, error: "Ese Messaging Service no es válido (empieza con MG)." };
  const entrada: ConectarEntrada = { accountSid, senderSid, reemplazarEntrada: f.reemplazarEntrada === true };
  const token = String(f.authToken || "").trim();
  if (token) entrada.authToken = token;
  if (servicio === SERVICIO_NUEVO) entrada.crearServicio = { nombre: nombreServicioNuevo(f.marca) };
  else entrada.messagingServiceSid = servicio;
  return { ok: true, entrada };
}

/** Texto de una advertencia de «Buscar en Twilio» (§6.2). Un código que no conocemos se muestra tal cual. */
export function textoAdvertencia(codigo: string, d?: Pick<Descubrimiento, "cuenta"> | null): string {
  switch (codigo) {
    case "cuenta_trial":
      return "Es una cuenta de prueba (Trial): Twilio no permite plantillas propias ni registrar un número. Pasala a pago (Upgrade) en la consola.";
    case "cuenta_inactiva":
      return `La cuenta de Twilio está ${d?.cuenta?.estado || "inactiva"}: no se puede usar.`;
    case "sin_numeros":
      return "Esta cuenta no tiene números de WhatsApp. Registralo en la consola (Messaging → Senders → WhatsApp Senders).";
    case "sin_numero_operativo":
      return "Ningún número está activo (ONLINE). Revisá el motivo en la consola.";
    case "muchos_servicios":
      return "La cuenta tiene más de 20 Messaging Services: mostramos los primeros 20.";
    default:
      return String(codigo || "");
  }
}

// ── La cuenta de Twilio queda atada al comercio ──────────────────────────────────────────────

/**
 * Lo que se dice ANTES de «Conectar» (o de «Guardar para la prueba» del Sandbox): guardar las credenciales ata el
 * Account SID a este comercio (índice único y ACCOUNT_SID_EN_USO en `guardarCredenciales`), y desde la app no hay
 * cómo desconectarlo. Conectar por error la cuenta de un cliente en el comercio de otro la dejaba inservible para
 * el dueño sin que nada lo hubiera advertido. `comercio` es el nombre que muestra el selector y `brandId` el
 * identificador (se agrega si es distinto: dos locales de una marca se llaman parecido).
 */
export function textoCuentaAtada(i: { accion: "conectar" | "guardar"; comercio: string; brandId?: string; cuenta?: string }): string {
  const comercio = String(i.comercio || i.brandId || "este comercio").trim();
  const id = String(i.brandId || "").trim();
  const quien = id && id.toLowerCase() !== comercio.toLowerCase() ? `«${comercio}» (${id})` : `«${comercio}»`;
  const cual = i.cuenta ? `esta cuenta de Twilio (${i.cuenta})` : "esta cuenta de Twilio";
  return `Al ${i.accion}, ${cual} queda atada a ${quien}: no se va a poder usar en otro comercio, y desde la app no se puede desconectar. Si es la cuenta de otro cliente, no sigas.`;
}

/** La cuenta encontrada ya está en otro comercio: no se dice cuál (es de otro cliente). */
export const TEXTO_CUENTA_EN_OTRO_COMERCIO =
  "Esta cuenta de Twilio ya está conectada a otro comercio, y una cuenta de Twilio es de un solo comercio. Usá la del cliente (o una subcuenta propia de este comercio).";

/** Cómo se nombra la cuenta encontrada en el aviso: el nombre que tiene en Twilio y el SID enmascarado. */
export function rotuloCuentaTwilio(c: { sid?: string | null; nombre?: string | null } | null | undefined): string {
  const sid = enmascararSid(c?.sid);
  const nombre = String(c?.nombre || "").trim();
  return nombre ? `«${nombre}», ${sid}` : sid;
}

// ── Errores con texto propio (§6.3) ──────────────────────────────────────────────────────────

export const TEXTO_CREDENCIALES_INVALIDAS = "Twilio rechazó el Account SID o el Auth Token. Copialos de nuevo desde la consola (Account → API keys & tokens). Tiene que ser el Auth Token, no una API Key. Si el token es el correcto y el 20003 aparece sólo al mandar, falta aprobar el Primary Customer Profile en Trust Hub.";
export const TEXTO_CUENTA_TRIAL = "Una cuenta Trial no puede mandar plantillas propias. Pasala a pago en la consola de Twilio y volvé a conectar.";
export const TEXTO_DEMASIADO_SEGUIDO = "Lo acabás de pedir: esperá unos segundos.";
/**
 * La marca de demostración (`hatsu-demo`; con `ACTIVA_COMERCIO_INICIAL` está oculta y el canje da la inicial) no carga
 * credenciales reales (`esMarcaDemoBloqueada` del backend). Antes se enteraba con un 422 MARCA_DEMO después de
 * pegar el Account SID y el token y tocar «Buscar en Twilio»: ahora el asistente lo dice al entrar, con
 * `cuenta.marcaDemoBloqueada`, y dice por dónde se cambia.
 */
export const TEXTO_MARCA_DEMO = "Estás en la marca de demostración: para conectar un WhatsApp real elegí la marca del cliente.";
/** Por dónde se cambia la marca: el selector de la fila de arriba (`OficialLayout`). */
export const TEXTO_DONDE_CAMBIAR_COMERCIO = "Se cambia en el menú, en «Marca».";
export const TEXTO_SANDBOX_JOIN = "Primero mandá \"join <tu código>\" al +1 415 523 8886 desde tu WhatsApp. La sesión dura 3 días.";
/**
 * 20003 con el token bueno (el backend lo confirma leyendo la cuenta con el mismo cliente): Twilio
 * frena los envíos por compliance. Mandar a revisar el token, como con CREDENCIALES_INVALIDAS, hacía
 * rotar un token que andaba.
 */
export const TEXTO_ENVIO_BLOQUEADO_COMPLIANCE = "Twilio acepta el token pero frena los envíos (20003): falta aprobar el Primary Customer Profile en Trust Hub. El token está bien, no hace falta cambiarlo: cuando Twilio apruebe el perfil, volvé a probar.";

/**
 * Texto para un error de la conexión por `code`. `error` es lo que dijo el backend: se usa si el
 * código no tiene texto propio. `codigoTwilio`, si viene, se agrega para que se pueda buscar.
 */
export function textoErrorConexion(x: { code?: string; error?: string; codigoTwilio?: unknown; status?: number }): string {
  const cod = typeof x?.codigoTwilio === "string" && x.codigoTwilio.trim() ? ` (código de Twilio ${x.codigoTwilio.trim()})` : "";
  switch (x?.code) {
    case "CREDENCIALES_INVALIDAS": return TEXTO_CREDENCIALES_INVALIDAS + cod;
    case "ENVIO_BLOQUEADO_COMPLIANCE": return TEXTO_ENVIO_BLOQUEADO_COMPLIANCE;
    case "CUENTA_TRIAL": return TEXTO_CUENTA_TRIAL;
    case "DEMASIADO_SEGUIDO": return TEXTO_DEMASIADO_SEGUIDO;
    case "MARCA_DEMO": return `${TEXTO_MARCA_DEMO} ${TEXTO_DONDE_CAMBIAR_COMERCIO}`;
    case "NO_KEY":
    case "CLAVE_INVALIDA":
      return "Falta WA_OFICIAL_CLAVE (base64 de 32 bytes) en el servidor. Sin ella el token no se puede guardar cifrado. Avisale a quien administra el deploy.";
    case "ACCOUNT_SID_EN_USO": return TEXTO_CUENTA_EN_OTRO_COMERCIO;
    case "CUENTA_NO_VERIFICADA": return "Primero conectá la cuenta de Twilio.";
    default: {
      const t = String(x?.error || "").trim();
      if (t) return t + cod;
      if (x?.status === 429) return TEXTO_DEMASIADO_SEGUIDO;
      // Sin texto del backend: el genérico por status (0 = no se llegó al servidor).
      return textoError({ status: typeof x?.status === "number" ? x.status : 0 }) + cod;
    }
  }
}

/**
 * Error del Sandbox. 63007 sale si el Sandbox no está activo EN ESA cuenta o si el token es de otra
 * cuenta o subcuenta (F24): por eso se dice qué Account SID se usó. Cualquier otro rechazo, casi
 * siempre, es que el teléfono no mandó el `join` (el código exacto no está confirmado, §8 D-7).
 */
export function textoErrorSandbox(codigoTwilio: string | null | undefined, accountSidUsado?: string | null): string {
  const c = String(codigoTwilio || "").trim();
  if (c === "63007") {
    return `El Sandbox no está activo en la cuenta de este Account SID (${enmascararSid(accountSidUsado) || "AC…"}). Activalo en la consola de esa misma cuenta o subcuenta: si lo activaste en otra, no sirve.`;
  }
  return c ? `${TEXTO_SANDBOX_JOIN} Twilio respondió con el código ${c}: https://www.twilio.com/docs/api/errors/${encodeURIComponent(c)}` : TEXTO_SANDBOX_JOIN;
}

/**
 * Texto de un error de «Mandarme un mensaje de prueba». Sólo un rechazo del Sandbox
 * (`SANDBOX_RECHAZO`) habla del «join»: un 20003 llega como `CREDENCIALES_INVALIDAS` y tiene que
 * mandar a revisar el token, no a unirse al Sandbox.
 */
export function textoFalloPruebaSandbox(
  x: { code?: string; error?: string; codigoTwilio?: unknown; status?: number },
  accountSidUsado?: string | null,
): string {
  if (x?.code === "SANDBOX_RECHAZO") return textoErrorSandbox(typeof x.codigoTwilio === "string" ? x.codigoTwilio : "", accountSidUsado);
  return textoErrorConexion(x);
}

// ── El aviso de la marca frenada, para quien lo lee ─────────────────────────────────────────

/**
 * Por qué se frenó la marca, dicho para el equipo sin jerga: el `motivo` del backend es para la tarjeta técnica de
 * Conexión (con el código), y en el aviso de todas las pantallas decía «(Meta retuvo y descartó envíos de la marca
 * (portfolio pacing))» o «(…Trust Hub, código 20003…)». Un código que no está acá cae en el motivo del backend tal cual.
 */
const MOTIVO_FRENO: Record<string, string> = {
  cumplimiento: "WhatsApp todavía no habilitó la cuenta para enviar: falta que aprueben el perfil de la empresa",
  cuenta_inactiva: "la cuenta del proveedor de WhatsApp no está activa",
  credenciales: "el proveedor de WhatsApp rechazó las credenciales de la cuenta",
  "135000": "Meta retuvo y descartó envíos de la marca por mandar demasiado rápido",
  "63038": "se llegó al límite de mensajes por día de la cuenta",
  ...Object.fromEntries(["63112", "63051", "63020", "63007"].map((c) => [c, "la cuenta o el número de WhatsApp no están habilitados"])),
};

/**
 * El aviso de la marca frenada que sale arriba de todas las pantallas. A una persona de un cliente: que los envíos
 * están en pausa y que lo revisa el equipo, sin motivo ni códigos (el motivo es de la cuenta de Deenex con el
 * proveedor y no tiene nada que hacer con él). Al equipo: el motivo dicho en castellano y qué falta (revisarla y
 * reanudarla, o sólo reanudarla si una prueba ya salió después de un freno por cumplimiento).
 */
export function textoMarcaFrenada(
  cuenta: Partial<Pick<CuentaPublica, "frenada">> | null | undefined,
  equipo: boolean,
): { titulo: string; detalle: string } | null {
  const f = cuenta?.frenada;
  if (!f?.activa) return null;
  if (!equipo) return { titulo: "Los envíos de WhatsApp están en pausa", detalle: "Por ahora no sale ningún mensaje. Lo está revisando el equipo de Deenex." };
  const porQue = MOTIVO_FRENO[String(f.codigo || "")] || String(f.motivo || "").trim();
  const falta = frenoSuperado(cuenta)
    ? "Una prueba ya salió después del freno: WhatsApp ya deja mandar y falta que alguien reanude la marca."
    : "Ningún mensaje sale hasta que alguien la revise y la reanude.";
  return { titulo: "La marca está frenada", detalle: `${porQue ? `${porQue.charAt(0).toUpperCase()}${porQue.slice(1)}. ` : ""}${falta}` };
}

// ── El freno por cumplimiento superado por una prueba del Sandbox ───────────────────────────

/**
 * Cuando un envío dio 20003 con el token bueno, la marca queda frenada por `cumplimiento` (falta Trust Hub). Si
 * después una prueba del Sandbox sale, Twilio ya deja mandar: la pantalla lo dice y ofrece reanudar con el motivo
 * cargado. Antes el diagnóstico seguía en rojo y nada lo decía. No se reanuda sola: la reanuda una persona, y la
 * reanudación es condicional al freno que vio la pantalla (`siFrenadaAt`).
 */
export const TITULO_FRENO_SUPERADO = "Twilio ya deja mandar: reanudá la marca";
export const TEXTO_FRENO_CAMBIO = "El freno cambió desde que abriste la pantalla: no reanudamos la marca. Mirá el motivo nuevo.";

/**
 * El freno superado, o null. Lo decide el backend (`frenoSuperadoPorPrueba`); acá se vuelve a mirar contra la cuenta
 * de ahora (fail-closed): sólo con la marca frenada por `cumplimiento`, por ESE freno (el mismo `at`) y con una prueba
 * posterior que tenga SID. `resultado` es lo que devolvió la prueba recién hecha, antes de que se recargue la cuenta.
 */
export function frenoSuperado(
  cuenta: Partial<Pick<CuentaPublica, "frenada">> | null | undefined,
  resultado?: Pick<ResultadoPruebaSandbox, "frenoSuperado"> | null,
): FrenoSuperado | null {
  const f = cuenta?.frenada;
  if (!f || f.activa !== true || f.codigo !== "cumplimiento" || !f.at) return null;
  const freno = Date.parse(f.at);
  if (!Number.isFinite(freno)) return null;
  for (const s of [f.superadaPorPrueba, resultado?.frenoSuperado]) {
    if (!s || typeof s !== "object") continue;
    const deEsteFreno = Date.parse(String(s.frenadaAt)) === freno;
    const prueba = Date.parse(String(s.pruebaAt));
    if (deEsteFreno && Number.isFinite(prueba) && prueba > freno && String(s.messageSid || "").trim()) return s;
  }
  return null;
}

/** Lo que se dice debajo del título: qué prueba, que la marca sigue frenada y que las campañas van una por una. */
export function textoFrenoSuperado(s: FrenoSuperado): string {
  return `La prueba del Sandbox del ${fechaHora(s.pruebaAt)} salió después del freno: Twilio ya no frena los envíos. La marca sigue frenada hasta que la reanudes, y después hay que reanudar cada campaña frenada, una por una.`;
}

/** El motivo que queda cargado (y se puede cambiar) al reanudar desde la prueba: queda en la auditoría. */
export function motivoReanudarTrasPrueba(s: FrenoSuperado): string {
  return `Prueba del Sandbox exitosa el ${fechaHora(s.pruebaAt)} (${s.messageSid}): Twilio ya deja mandar.`;
}

/** Estados en los que la prueba del Sandbox ya no va a cambiar (se deja de preguntar). */
export function sandboxTerminado(estado: string | null | undefined): boolean {
  return ["delivered", "read", "failed", "undelivered", "entregado", "leido", "fallido", "no_entregado"].includes(String(estado || "").toLowerCase());
}

// ── Paso 3: resultado de conectar ────────────────────────────────────────────────────────────

/** El paso `entrada` que pide confirmar antes de pisar la URL de otra aplicación, si lo hay. */
export function pasoAConfirmar(pasos: PasoConexion[] | null | undefined): PasoConexion | null {
  return (pasos || []).find((p) => p?.estado === "requiere_confirmacion") ?? null;
}

/** true si algún paso de conectar quedó en error (el 200 de conectar no dice que salió bien). */
export function conexionConErrores(pasos: PasoConexion[] | null | undefined): boolean {
  return (pasos || []).some((p) => p?.estado === "error");
}

// ── Diagnóstico ──────────────────────────────────────────────────────────────────────────────

export type IconoPaso = "ok" | "error" | "advertencia" | "pendiente" | "omitido" | "no_aplica" | "confirmar";

/**
 * Ícono, color y texto corto de un estado (de un paso de conectar o del diagnóstico). Un estado
 * desconocido se pinta como advertencia: nunca en verde.
 */
export function iconoPaso(estado: EstadoPaso | PasoConexion["estado"] | string): { icono: IconoPaso; cls: string; label: string } {
  switch (estado) {
    case "ok": return { icono: "ok", cls: "text-emerald-600", label: "Listo" };
    case "error": return { icono: "error", cls: "text-red-600", label: "Error" };
    case "advertencia": return { icono: "advertencia", cls: "text-amber-600", label: "Revisar" };
    case "pendiente": return { icono: "pendiente", cls: "text-sky-600", label: "Esperando" };
    case "omitido": return { icono: "omitido", cls: "text-slate-400", label: "No se hizo" };
    case "no_aplica": return { icono: "no_aplica", cls: "text-slate-400", label: "No aplica" };
    case "requiere_confirmacion": return { icono: "confirmar", cls: "text-amber-600", label: "Falta confirmar" };
    default: return { icono: "advertencia", cls: "text-amber-600", label: "Revisar" };
  }
}

export const GRUPOS: readonly { grupo: GrupoPaso; titulo: string }[] = [
  { grupo: "servidor", titulo: "Servidor" },
  { grupo: "cuenta", titulo: "Cuenta" },
  { grupo: "numero", titulo: "Número" },
  { grupo: "recepcion", titulo: "Recepción" },
  { grupo: "cumplimiento", titulo: "Cumplimiento" },
  { grupo: "costos", titulo: "Costos" },
];

/** Pasos bloqueantes en error, contados desde los pasos (no desde lo que diga el resumen). */
export function bloqueantesEnError(d: Pick<Diagnostico, "pasos"> | null | undefined): PasoDiagnostico[] {
  return (d?.pasos || []).filter((p) => p?.bloqueante === true && p.estado === "error");
}

/**
 * Semáforo del diagnóstico. «Listo para enviar» SÓLO si el backend lo dice Y ningún paso
 * bloqueante está en error: si las dos fuentes no coinciden, gana la más estricta (fail-closed).
 */
export function resumenDiagnostico(d: Diagnostico | null | undefined): { listo: boolean; titulo: string; n: number } {
  if (!d) return { listo: false, titulo: "Todavía no se revisó la conexión", n: 0 };
  const enError = bloqueantesEnError(d).length;
  const declarados = Array.isArray(d.bloqueantes) ? d.bloqueantes.length : 0;
  const n = Math.max(enError, declarados);
  const listo = d.listoParaEnviar === true && n === 0;
  if (listo) return { listo, titulo: "Listo para enviar", n: 0 };
  if (n === 0) return { listo, titulo: "Todavía no está listo para enviar", n };
  return { listo, titulo: n === 1 ? "Hay 1 cosa que impide enviar" : `Hay ${num(n)} cosas que impiden enviar`, n };
}

/** Una fecha ISO completa dentro de un texto del backend («Último recibo: 2026-09-23T09:11:32.950Z»). */
const RE_FECHA_ISO = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})/g;

/** El detalle de un paso con las fechas ISO pasadas a la hora del navegador en es-AR (el backend las manda crudas). */
export function detalleLegible(detalle: string | null | undefined): string {
  return String(detalle || "").replace(RE_FECHA_ISO, (iso) => (Number.isFinite(Date.parse(iso)) ? fechaHora(iso) : iso));
}

/** El único paso que «Volver a revisar» deja esperando algo que llega solo: el evento de prueba que Twilio manda al sink. */
export const PASO_PRUEBA_SINK = "recepcion.prueba_twilio";

/**
 * true si algún paso espera algo que tiene que llegar solo en unos segundos: sólo la prueba del sink.
 * Con una cuenta real sin número, el servicio, la entrada y Meta (sin envíos reales) también quedan en
 * «pendiente», pero no cambian solos: preguntar cada 3 s durante 60 s no los mueve, y el «Esperando el
 * aviso de prueba de Twilio…» no esperaba nada.
 */
export function hayPendientes(d: Diagnostico | null | undefined): boolean {
  return (d?.pasos || []).some((p) => p?.estado === "pendiente" && p.id === PASO_PRUEBA_SINK);
}

/**
 * Los pasos por grupo, en el orden de la pantalla. Un grupo arranca abierto si tiene algo para
 * hacer (error, advertencia o pendiente). Un grupo que el front no conoce va al final: un paso
 * nuevo del backend se muestra igual, no se pierde.
 */
export function agruparPasos(d: Diagnostico | null | undefined): { grupo: string; titulo: string; pasos: PasoDiagnostico[]; abierto: boolean; conteo: Record<string, number> }[] {
  const pasos = d?.pasos || [];
  const conocidos = GRUPOS.map((g) => g.grupo as string);
  const extra = Array.from(new Set(pasos.map((p) => String(p.grupo)).filter((g) => !conocidos.includes(g))));
  const lista = [...GRUPOS.map((g) => ({ grupo: g.grupo as string, titulo: g.titulo })), ...extra.map((g) => ({ grupo: g, titulo: g }))];
  return lista
    .map(({ grupo, titulo }) => {
      const delGrupo = pasos.filter((p) => String(p.grupo) === grupo);
      const conteo: Record<string, number> = {};
      for (const p of delGrupo) conteo[p.estado] = (conteo[p.estado] || 0) + 1;
      const abierto = delGrupo.some((p) => p.estado === "error" || p.estado === "advertencia" || p.estado === "pendiente");
      return { grupo, titulo, pasos: delGrupo, abierto, conteo };
    })
    .filter((g) => g.pasos.length > 0);
}

/** El estado de un paso por id, o null si el diagnóstico no lo trae. */
export function estadoDePaso(d: Diagnostico | null | undefined, id: string): EstadoPaso | null {
  return (d?.pasos || []).find((p) => p?.id === id)?.estado ?? null;
}

export type AccionUI =
  | { tipo: "autoconfigurar"; etiqueta: string }
  | { tipo: "enlace"; url: string; etiqueta: string }
  | { tipo: "copiar"; nombre: string; etiqueta: string }
  | { tipo: "casilla"; clave: ClaveConfirmacionManual; etiqueta: string; marcado: boolean }
  | { tipo: "cambiar_token"; etiqueta: string }
  | { tipo: "navegar"; ruta: string; etiqueta: string }
  | { tipo: "reanudar_marca"; etiqueta: string; superado: FrenoSuperado };

const RUTA_CONFIG = "/activa/oficial/configuracion";
const SECCIONES = new Set(["cuenta", "envio", "bajas", "saldo"]);
const VARIABLE = /^[A-Z][A-Z0-9_]{1,63}$/;

/** Sólo enlaces https: un `javascript:` o un http plano que llegara en la acción no se pinta. */
export function urlSegura(url: unknown): string | null {
  if (typeof url !== "string") return null;
  try {
    const u = new URL(url);
    return u.protocol === "https:" ? u.toString() : null;
  } catch {
    return null;
  }
}

/** Valor actual de una confirmación manual (la casilla «Ya lo hice»). */
export function confirmacionActual(clave: ClaveConfirmacionManual, cuenta: Pick<CuentaPublica, "config" | "portfolioVerificado"> | null | undefined): boolean {
  if (!cuenta) return false;
  if (clave === "portfolioVerificado") return cuenta.portfolioVerificado === true;
  return cuenta.config?.[clave] === true;
}

/**
 * El botón (o casilla, o enlace) que lleva un paso del diagnóstico. null = nada para hacer.
 * - Un paso en ok o no_aplica no lleva botón, salvo la casilla manual (así se puede desmarcar).
 * - Una acción mal formada (URL que no es https, variable rara, sección desconocida) no se pinta.
 * - «Reanudar la marca» sólo si la cuenta de ahora confirma el freno superado por una prueba (`frenoSuperado`).
 */
export function accionDePaso(
  p: PasoDiagnostico,
  cuenta?: (Pick<CuentaPublica, "config" | "portfolioVerificado"> & Partial<Pick<CuentaPublica, "frenada">>) | null,
): AccionUI | null {
  const a = p?.accion as AccionPaso | undefined;
  if (!a || typeof a !== "object") return null;
  if (a.tipo === "confirmar_manual") {
    if (a.clave !== "advancedOptOutConfirmado" && a.clave !== "complianceConfirmado" && a.clave !== "portfolioVerificado") return null;
    return { tipo: "casilla", clave: a.clave, etiqueta: "Ya lo hice", marcado: confirmacionActual(a.clave, cuenta) };
  }
  if (p.estado === "ok" || p.estado === "no_aplica") return null;
  switch (a.tipo) {
    case "autoconfigurar":
      return { tipo: "autoconfigurar", etiqueta: "Arreglarlo automáticamente" };
    case "consola": {
      const url = urlSegura(a.url);
      return url ? { tipo: "enlace", url, etiqueta: String(a.etiqueta || "").trim() || "Abrir la consola de Twilio" } : null;
    }
    case "variable_entorno": {
      if (!VARIABLE.test(String(a.nombre || ""))) return null;
      // El modo de envío en simulado no es una falta: la variable está, en `simulated`, a propósito (sin ella el
      // servidor mandaría de verdad). Decía «Falta en el servidor: WA_OFICIAL_TRANSPORT». Se dice qué poner para
      // pasar a real, y eso es lo que se copia.
      if (p.id === "servidor.modo") return { tipo: "copiar", nombre: `${a.nombre}=twilio`, etiqueta: `Para mandar de verdad: ${a.nombre}=twilio` };
      return { tipo: "copiar", nombre: a.nombre, etiqueta: `Falta en el servidor: ${a.nombre}` };
    }
    case "config":
      return SECCIONES.has(a.seccion) ? { tipo: "navegar", ruta: `${RUTA_CONFIG}?seccion=${a.seccion}`, etiqueta: "Ir a Configuración" } : null;
    case "rotar_token":
      return { tipo: "cambiar_token", etiqueta: "Cambiar token" };
    case "reanudar_marca": {
      // El botón necesita el freno y la prueba de la cuenta: si la cuenta no lo confirma, no se pinta.
      const superado = frenoSuperado(cuenta);
      return superado ? { tipo: "reanudar_marca", etiqueta: "Reanudar la marca", superado } : null;
    }
    default:
      return null;
  }
}

/**
 * Hasta cuándo se sigue preguntando por el diagnóstico después de «Volver a revisar»: cada 3 s
 * durante 60 s, y sólo mientras la prueba del sink esté pendiente (§B.7.4, `hayPendientes`). Devuelve el
 * intervalo o null.
 */
export const POLLING_DIAGNOSTICO_MS = 3000;
export const TOPE_POLLING_DIAGNOSTICO_MS = 60_000;
export function intervaloDiagnostico(d: Diagnostico | null | undefined, desde: number | null, ahora: number): number | null {
  if (desde === null || !hayPendientes(d)) return null;
  return ahora - desde < TOPE_POLLING_DIAGNOSTICO_MS ? POLLING_DIAGNOSTICO_MS : null;
}

// ── Cuenta ya conectada ──────────────────────────────────────────────────────────────────────

export const TEXTO_VERIFICADA_EN_OTRO_MODO = "La cuenta se verificó en modo simulado: conectá la cuenta real de Twilio.";

/** Lo que hace falta para saber si una cuenta es del modo de hoy (la CuentaPublica o la cuenta del Resumen). */
type CuentaConModo = Partial<Pick<CuentaPublica, "estado" | "modo" | "modoTransporte" | "verificadaEnOtroModo">>;

/** Verificada en simulado con el servidor en twilio, lo diga el flag del backend nuevo o el `modo` del anterior. */
function verificadaEnOtroModo(c: CuentaConModo, modo?: ModoTransporte | null): boolean {
  if (c.verificadaEnOtroModo === true) return true;
  return c.estado === "verificada" && (modo ?? c.modoTransporte) === "twilio" && c.modo === "simulado";
}

/**
 * ¿La cuenta está conectada con el modo de HOY? Espejo de `cuentaDelModo` del backend
 * (entorno.util.ts): verificada y, en twilio, no verificada en simulado (esa verificación no habló con
 * Twilio: el número y la entrada son del simulador). En simulado no se exige nada más: es la demo.
 */
export function cuentaDelModo(c: CuentaConModo | null | undefined, modo?: ModoTransporte | null): boolean {
  if (!c || c.estado !== "verificada") return false;
  return !verificadaEnOtroModo(c, modo);
}

/** El aviso para una cuenta verificada en simulado con el servidor en twilio, o null. */
export function avisoOtroModo(c: CuentaConModo | null | undefined): string | null {
  return c && verificadaEnOtroModo(c) ? TEXTO_VERIFICADA_EN_OTRO_MODO : null;
}

/**
 * «Conectada a AC…1a2b (Full) · número +54 9 11… · servicio Deenex · Hatsu». Sin el nombre del
 * servicio (cuenta conectada antes de que se guardara), el MG enmascarado. null si la cuenta no
 * está conectada (sin verificar, sin número, o verificada en simulado con el servidor en twilio):
 * ahí se muestra el formulario.
 */
export function resumenConexion(c: CuentaPublica | null | undefined): string | null {
  if (!c || c.id === null || !cuentaDelModo(c)) return null;
  const partes: string[] = [];
  const tipo = c.twilio?.tipoCuenta ? ` (${c.twilio.tipoCuenta})` : "";
  partes.push(`Conectada a ${c.accountSidEnmascarado || "la cuenta de Twilio"}${tipo}`);
  if (c.senderE164) partes.push(`número ${c.senderE164}`);
  const nombreServicio = typeof c.twilio?.nombreServicio === "string" ? c.twilio.nombreServicio.trim() : "";
  if (nombreServicio) partes.push(`servicio ${nombreServicio}`);
  else if (c.messagingServiceSid) partes.push(`servicio ${enmascararSid(c.messagingServiceSid)}`);
  return partes.join(" · ");
}
